import './ManualUpload.css'
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { apiFetch } from '../../utils/apiClient';
import { API_BASE_URL } from '../../config/api';
import { downloadManualUploadExcel } from '../../utils/manualUploadExport';

// --- ICONS (self-contained in this file) ---

const WaterQualityIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const LabIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547a2 2 0 00-.547 1.806l.477 2.387a6 6 0 00.517 3.86l.158.318a6 6 0 00.517 3.86l2.387.477a2 2 0 001.806.547a2 2 0 00.547-1.806l-.477-2.387a6 6 0 00-.517-3.86l-.158-.318a6 6 0 00-.517-3.86l-2.387-.477a2 2 0 01-.547-1.806zM15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4H7z" />
  </svg>
);

const InventoryIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
);

const RepairIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);


const CITY_MAP = {
  linshui: { displayName: '邻水', siteKey: 'default' },
  jinghong: { displayName: '景洪', siteKey: 'jinghong' },
};


/** 与小程序 `pages/entry` 中 water_online 提交结构一致：键名、展示名、单位 */
const WATER_ONLINE_DISPLAY_SCHEMA = [
  { name: 'PH', label: 'pH值', unit: '' },
  { name: 'NH3N', label: '氨氮 (mg/L)', unit: 'mg/L' },
  { name: 'CODCr', label: 'COD (mg/L)', unit: 'mg/L' },
];

function waterOnlineVariableMap(items) {
  const m = new Map();
  (items || []).forEach((v) => {
    const k = String(v && v.name != null ? v.name : '').trim();
    if (!k) return;
    m.set(k.toLowerCase(), v);
  });
  return m;
}

/** 与小程序 `buildPayload` repair_order 中 devices 顺序一致 */
const REPAIR_DEVICE_ORDER = ['鼓风机', '提升泵', '搅拌机', '加药设备'];

function sortRepairDevices(devices) {
  const list = Array.isArray(devices) ? [...devices] : [];
  return list.sort((a, b) => {
    const na = String(a && a.name != null ? a.name : '').trim();
    const nb = String(b && b.name != null ? b.name : '').trim();
    const ia = REPAIR_DEVICE_ORDER.indexOf(na);
    const ib = REPAIR_DEVICE_ORDER.indexOf(nb);
    if (ia === -1 && ib === -1) return na.localeCompare(nb, 'zh-CN');
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

/**
 * 景洪项目设备台账（与提供资料一致）；小程序按此表逐台提交后，「故障情况 / 是否维修」与 device.name 一一对应。
 * purpose: 用途；qty: 数量（台）；powerKw: 功率（kw）；hoursPerDay: 使用时长（h/天）；kwhMonth: 耗电量（kw/h·月）
 */
const JINGHONG_EQUIPMENT_CATALOG = [
  { name: '潜水搅拌器', purpose: 'A1池搅拌', qty: 1, powerKw: 5, hoursPerDay: 24, kwhMonth: 3600 },
  { name: '潜水搅拌器', purpose: 'A2池搅拌', qty: 1, powerKw: 1.5, hoursPerDay: 24, kwhMonth: 1080 },
  { name: '潜水搅拌器', purpose: '生化污泥池搅拌', qty: 1, powerKw: 0.85, hoursPerDay: 10, kwhMonth: 255 },
  { name: '潜水搅拌器', purpose: '化学污泥池搅拌', qty: 1, powerKw: 0.85, hoursPerDay: 5, kwhMonth: 127.5 },
  { name: '调节池出水泵', purpose: '提升至A1池', qty: 2, powerKw: 3, hoursPerDay: 16, kwhMonth: 1440 },
  { name: '物理消泡泵', purpose: '01、02池消泡', qty: 2, powerKw: 11, hoursPerDay: 2, kwhMonth: 660 },
  { name: '一级硝化液回流泵', purpose: '01回流至A1池', qty: 2, powerKw: 11, hoursPerDay: 24, kwhMonth: 7920 },
  { name: '冷却塔污水循环泵', purpose: '生化池降温', qty: 2, powerKw: 11, hoursPerDay: 24, kwhMonth: 7920 },
  { name: '冷却塔清水循环泵', purpose: '生化池降温', qty: 2, powerKw: 9.2, hoursPerDay: 24, kwhMonth: 6624 },
  { name: '冷却塔风扇', purpose: '生化池降温', qty: 1, powerKw: 5, hoursPerDay: 24, kwhMonth: 3600 },
  { name: '二级硝化液回流泵', purpose: '02回流至A2池', qty: 2, powerKw: 4, hoursPerDay: 24, kwhMonth: 2880 },
  { name: '超滤进水泵', purpose: '进膜提升水泵', qty: 2, powerKw: 11, hoursPerDay: 16, kwhMonth: 5280 },
  { name: '超滤循环水泵', purpose: '膜循环水泵', qty: 1, powerKw: 55, hoursPerDay: 16, kwhMonth: 26400 },
  { name: '超滤清洗水泵', purpose: '清洗膜', qty: 1, powerKw: 7.5, hoursPerDay: 2, kwhMonth: 450 },
  { name: '一级芬顿进水泵', purpose: '一芬进水', qty: 2, powerKw: 1.5, hoursPerDay: 16, kwhMonth: 720 },
  { name: '1#集水井提升泵', purpose: '收集加药间污水', qty: 2, powerKw: 3, hoursPerDay: 1, kwhMonth: 90 },
  { name: '2#集水井提升泵', purpose: '压滤机清水提升至A1', qty: 2, powerKw: 3, hoursPerDay: 3, kwhMonth: 270 },
  { name: '二级芬顿提升泵', purpose: '二芬进水', qty: 2, powerKw: 3, hoursPerDay: 16, kwhMonth: 1440 },
  { name: '一级BAF回流泵', purpose: '一级BAF回流', qty: 1, powerKw: 3, hoursPerDay: 24, kwhMonth: 2160 },
  { name: '二级BAF回流泵', purpose: '二级BAF回流', qty: 2, powerKw: 1.5, hoursPerDay: 0, kwhMonth: 0 },
  { name: '清水外排泵', purpose: '清水外排', qty: 2, powerKw: 11, hoursPerDay: 1, kwhMonth: 330 },
  { name: 'BAF反洗泵', purpose: 'BAF反洗', qty: 2, powerKw: 11, hoursPerDay: 0.1, kwhMonth: 33 },
  { name: '螺杆泵', purpose: '板框进泥', qty: 4, powerKw: 3, hoursPerDay: 10, kwhMonth: 900 },
  { name: '压榨泵', purpose: '板框水压榨', qty: 2, powerKw: 4, hoursPerDay: 2, kwhMonth: 240 },
  { name: '板框空压机', purpose: '板框', qty: 1, powerKw: 7.5, hoursPerDay: 3, kwhMonth: 675 },
  { name: '板框保压泵', purpose: '板框', qty: 2, powerKw: 5.5, hoursPerDay: 1, kwhMonth: 165 },
  { name: '板框清洗泵', purpose: '滤布清洗', qty: 1, powerKw: 15, hoursPerDay: 1, kwhMonth: 450 },
  { name: '叠螺脱水机', purpose: '污泥脱水', qty: 1, powerKw: 1.65, hoursPerDay: 12, kwhMonth: 594 },
  { name: '双氧水计量泵', purpose: '芬顿药剂', qty: 3, powerKw: 0.25, hoursPerDay: 16, kwhMonth: 240 },
  { name: '亚铁计量泵', purpose: '芬顿药剂', qty: 4, powerKw: 0.37, hoursPerDay: 16, kwhMonth: 355 },
  { name: '碳源计量泵', purpose: 'A池药剂', qty: 6, powerKw: 0.37, hoursPerDay: 16, kwhMonth: 532 },
  { name: '片碱计量泵', purpose: '芬顿药剂', qty: 3, powerKw: 0.25, hoursPerDay: 16, kwhMonth: 240 },
  { name: 'PAM计量泵', purpose: '芬顿药剂', qty: 5, powerKw: 0.25, hoursPerDay: 16, kwhMonth: 240 },
  { name: 'PAC计量泵', purpose: '压泥', qty: 2, powerKw: 0.25, hoursPerDay: 0, kwhMonth: 0 },
  { name: '生化风机', purpose: '生化曝气', qty: 2, powerKw: 90, hoursPerDay: 24, kwhMonth: 64800 },
  { name: '芬顿-BAF风机', purpose: '芬顿-BAF曝气', qty: 1, powerKw: 11, hoursPerDay: 24, kwhMonth: 7920 },
];

const JINGHONG_MINI_EMPTY = { fault: '否', repair: '否' };

/** 与小程序提交 JSON 中 device.name 一致：`设备名（用途）` */
function jinghongSubmitDeviceLabel(row) {
  return `${String(row.name || '').trim()}（${String(row.purpose || '').trim()}）`;
}

/** 从最近一条 repair_order 解析每台设备的故障/维修（键为 device.name） */
function extractJinghongPerDeviceRepairsFromLatest(latest) {
  const map = new Map();
  if (!latest || !Array.isArray(latest.sections)) return map;
  for (const sec of latest.sections) {
    for (const dev of sec.devices || []) {
      const label = String(dev.name || '').trim();
      if (!label) continue;
      const it = (dev.items && dev.items[0]) || {};
      const fault = it.fault != null && String(it.fault).trim() !== '' ? String(it.fault) : '—';
      const repair = it.repair != null && String(it.repair).trim() !== '' ? String(it.repair) : '—';
      map.set(label, { fault, repair });
    }
  }
  return map;
}

const PROCESS_DAILY_GROUPS = [
  { title: "原水", cols: [
    { name: "raw_NH3N", label: "氨氮" },
    { name: "raw_COD", label: "COD" },
    { name: "raw_TN", label: "总氮" },
  ]},
  { title: "一级反硝化", cols: [
    { name: "d1_NH3N", label: "氨氮" },
    { name: "d1_COD", label: "COD" },
  ]},
  { title: "一级硝化", cols: [
    { name: "n1_NH3N", label: "氨氮" },
    { name: "n1_COD", label: "COD" },
    { name: "n1_SV30", label: "SV30" },
  ]},
  { title: "二级硝化", cols: [
    { name: "n2_NH3N", label: "氨氮" },
    { name: "n2_COD", label: "COD" },
    { name: "n2_SV30", label: "SV30" },
  ]},
  { title: "二级好氧", cols: [
    { name: "ae2_NH3N", label: "氨氮" },
    { name: "ae2_COD", label: "COD" },
  ]},
  { title: "总排放口", cols: [
    { name: "out_NH3N", label: "氨氮" },
    { name: "out_COD", label: "COD" },
    { name: "out_TN", label: "总氮" },
  ]},
  { title: "调节池空高(m)", cols: [
    { name: "tank_freeboard_m", label: "空高" },
  ]},
  { title: "备注", cols: [
    { name: "remarks", label: "内容" },
  ]},
];

function pickProcessDailyCell(submission, itemName) {
  const sections = submission?.sections || [];
  for (let s = 0; s < sections.length; s++) {
    const items = sections[s].items || [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it && it.name === itemName) {
        const v = it.value;
        if (v === null || v === undefined || v === "") return "";
        return String(v);
      }
    }
  }
  return "";
}

function pad2Drug(n) {
  return `${Number(n)}`.padStart(2, "0");
}

function submissionFillYmdForDrug(fillTime, createdAt) {
  const raw =
    fillTime != null && `${fillTime}`.trim() !== ""
      ? String(fillTime).trim()
      : String(createdAt || "").trim();
  if (!raw) return null;
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const slash = raw.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})/);
  if (slash) {
    return `${slash[1]}-${pad2Drug(slash[2])}-${pad2Drug(slash[3])}`;
  }
  const d = new Date(raw.includes("T") ? raw : raw.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${pad2Drug(d.getMonth() + 1)}-${pad2Drug(d.getDate())}`;
}

function displayFillYmd(fillTime, createdAt) {
  return submissionFillYmdForDrug(fillTime, createdAt) || "—";
}

function todayYmdForDrug() {
  const t = new Date();
  return `${t.getFullYear()}-${pad2Drug(t.getMonth() + 1)}-${pad2Drug(t.getDate())}`;
}

function currentYmLocal() {
  return todayYmdForDrug().slice(0, 7);
}

function daysInMonthCalendar(year, month) {
  return new Date(year, month, 0).getDate();
}

function parseViewYm(ym) {
  const [yyStr, mmStr] = String(ym || currentYmLocal()).split("-");
  const yy = Number(yyStr);
  const mm = Number(mmStr);
  return {
    yyStr,
    mmStr,
    yy,
    mm,
    daysInMonth: daysInMonthCalendar(yy, mm),
    monthTitle: `${yyStr}年${Number(mmStr)}月`,
  };
}

function isFutureDayRow(ymdKey, viewYm, todayYmd) {
  if (String(viewYm) !== String(todayYmd).slice(0, 7)) return false;
  return ymdKey > todayYmd;
}

function chartCapYmdForViewYm(viewYm) {
  const today = todayYmdForDrug();
  const ym = viewYm || currentYmLocal();
  if (ym === today.slice(0, 7)) return today;
  const { yyStr, mmStr, daysInMonth } = parseViewYm(ym);
  return `${yyStr}-${mmStr}-${pad2Drug(daysInMonth)}`;
}

function monthDayAxisTicks(cap) {
  const n = Math.max(Number(cap) || 31, 1);
  return Array.from({ length: n }, (_, i) => i + 1);
}

function chartWidthForMonthDays(cap, padL, padR, minPerDay = 20, minW = 680) {
  const c = Math.max(Number(cap) || 31, 1);
  return Math.max(minW, padL + padR + c * minPerDay);
}

function formatNationalDayLabel(ymd) {
  const m = String(ymd).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return String(ymd);
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(dt.getTime())) return String(ymd);
  return dt.toLocaleDateString("zh-CN", { year: "numeric", month: "long", day: "numeric" });
}

function isApprovedSubmission(sub) {
  return String(sub?.status || "").trim() === "已通过";
}

function approvedSubmissionsOnly(list) {
  return (list || []).filter(isApprovedSubmission);
}

function sortSubsNewestFirst(list) {
  return [...(list || [])].sort(
    (a, b) =>
      new Date(b.fillTime || b.createdAt || 0) - new Date(a.fillTime || a.createdAt || 0)
  );
}

function submissionHasFlow(sub, kind) {
  const key = kind === "in" ? "inQuantity" : "outQuantity";
  return (sub?.records || []).some((r) => String(r?.[key] ?? "").trim() !== "");
}

/** mode: onDay=精确到日；asOf=不晚于该日。kind 可选，限定入/出侧有填写 */
function pickApprovedDrugSubmission(submissions, { ymd, kind, mode = "onDay" } = {}) {
  if (!Array.isArray(submissions) || !submissions.length) return null;
  const day = ymd || todayYmdForDrug();
  const eligible = submissions.filter((s) => {
    if (!isApprovedSubmission(s)) return false;
    const y = submissionFillYmdForDrug(s.fillTime, s.createdAt);
    if (!y) return false;
    if (mode === "onDay" ? y !== day : y > day) return false;
    if (kind && !submissionHasFlow(s, kind)) return false;
    return true;
  });
  return eligible.length ? sortSubsNewestFirst(eligible)[0] : null;
}

/** 所选自然月内、截至 asOf 日的累计入/出库（仅已通过） */
function sumDrugMonthFlowQty(submissions, drugName, unit, kind, ym, asOfYmd) {
  const name = String(drugName || "").trim();
  const u = String(unit || "").trim();
  const month = String(ym || "").slice(0, 7);
  if (!month) return null;
  const { yyStr, mmStr, daysInMonth } = parseViewYm(month);
  const monthEnd = `${yyStr}-${mmStr}-${pad2Drug(daysInMonth)}`;
  const asOf = asOfYmd || monthEnd;
  const cap = asOf.startsWith(month) ? (asOf < monthEnd ? asOf : monthEnd) : monthEnd;
  let sum = 0;
  let hasAny = false;
  for (const sub of submissions || []) {
    if (!isApprovedSubmission(sub)) continue;
    const y = submissionFillYmdForDrug(sub.fillTime, sub.createdAt);
    if (!y || !y.startsWith(month) || y > cap) continue;
    for (const r of sub.records || []) {
      if (String(r.drugName || "").trim() !== name) continue;
      if (String(r.unit || "").trim() !== u) continue;
      const raw = kind === "in" ? r.inQuantity : r.outQuantity;
      if (raw == null || String(raw).trim() === "") continue;
      const n = Number(raw);
      if (!Number.isFinite(n)) continue;
      sum += n;
      hasAny = true;
    }
  }
  return hasAny ? sum : null;
}

function drugNameUnitKey(name, unit) {
  return `${String(name || "").trim()}\t${String(unit || "").trim()}`;
}

function drugStockByNameTabKeyMap(submissions) {
  const m = new Map();
  for (const sub of submissions || []) {
    for (const r of sub.records || []) {
      const nk = drugNameUnitKey(r.drugName, r.unit);
      if (!m.has(nk) && r.stockQuantity != null && r.stockQuantity !== "") {
        m.set(nk, r.stockQuantity);
      }
    }
  }
  return m;
}

function buildDrugInventoryTabRows(catalog, submission, kind, allSubmissions, viewYm, asOfYmd) {
  const recMap = new Map();
  (submission?.records || []).forEach((r) => {
    recMap.set(drugNameUnitKey(r.drugName, r.unit), r);
  });
  const stockMap = drugStockByNameTabKeyMap(allSubmissions);
  const ym = String(viewYm || "").slice(0, 7);
  return (catalog || []).map((c) => {
    const name = String(c.drugName || "").trim();
    const unit = String(c.unit || "").trim();
    const nk = drugNameUnitKey(name, unit);
    const rec = recMap.get(nk);
    const stock = rec?.stockQuantity ?? stockMap.get(nk);
    const flowQty = kind === "in" ? rec?.inQuantity : rec?.outQuantity;
    const monthCumQty = sumDrugMonthFlowQty(allSubmissions, name, unit, kind, ym, asOfYmd);
    return { drugName: c.drugName, unit: c.unit, flowQty, monthCumQty, stockQuantity: stock };
  });
}

function formatDrugStockCell(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

const LAB_ENTRY_OPTIONS = PROCESS_DAILY_GROUPS.filter((g) => g.title !== "备注")
  .flatMap((g) =>
    g.cols
      .filter((c) => c.name !== "remarks")
      .map((c) => ({ itemName: c.name, optionLabel: `${g.title} · ${c.label}` }))
  );

function parseChartScalar(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (s === "" || s === "—" || s === "-" || s === "未填写") return null;
  const n = Number.parseFloat(s.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function ymdPointsDaySeries(ymdToY, ym, todayCapYmd) {
  const [yyStr, mmStr] = String(ym || "").split("-");
  const yy = Number(yyStr);
  const mm = Number(mmStr);
  if (!yy || !mm) return [];
  const dim = daysInMonthCalendar(yy, mm);
  const pts = [];
  for (let d = 1; d <= dim; d++) {
    const key = `${yyStr}-${mmStr}-${pad2Drug(d)}`;
    if (todayCapYmd && key > todayCapYmd) break;
    if (ymdToY.has(key)) pts.push({ day: d, y: ymdToY.get(key) });
  }
  return pts;
}

/** 已通过提交按日去重取标量，再转月折线点 */
function buildMonthPointsFromSubs(subs, ym, getY) {
  const perDay = new Map();
  for (const sub of sortSubsNewestFirst(subs)) {
    if (!isApprovedSubmission(sub)) continue;
    const ymd = submissionFillYmdForDrug(sub.fillTime, sub.createdAt);
    if (!ymd || !ymd.startsWith(ym) || perDay.has(ymd)) continue;
    const y = getY(sub);
    if (y == null) continue;
    perDay.set(ymd, y);
  }
  return ymdPointsDaySeries(perDay, ym, chartCapYmdForViewYm(ym));
}

function buildWaterOnlineMonthPointsForVar(history, ym, varNameLower) {
  return buildMonthPointsFromSubs(history, ym, (sub) => {
    const v = waterOnlineVariableMap(sub.variables || []).get(varNameLower);
    return v ? parseChartScalar(v.value) : null;
  });
}

function buildWaterLabMonthPointsForItem(history, ym, itemName) {
  return buildMonthPointsFromSubs(history, ym, (sub) =>
    parseChartScalar(pickProcessDailyCell(sub, itemName))
  );
}

function pickCatalogRowByKey(catalog, drugKey) {
  if (!Array.isArray(catalog) || !catalog.length) return null;
  if (drugKey) {
    const hit = catalog.find((c) => (c.drugKey || c.slug) === drugKey);
    if (hit) return hit;
  }
  return catalog[0];
}

function buildDrugMonthPointsForCatalogRow(submissions, catalogRow, kind, ym) {
  if (!catalogRow || !Array.isArray(submissions)) return [];
  const name = String(catalogRow.drugName || "").trim();
  const unit = String(catalogRow.unit || "").trim();
  return buildMonthPointsFromSubs(submissions, ym, (sub) => {
    const rec = (sub.records || []).find(
      (r) => String(r.drugName || "").trim() === name && String(r.unit || "").trim() === unit
    );
    if (!rec) return null;
    return parseChartScalar(kind === "in" ? rec.inQuantity : rec.outQuantity);
  });
}

function MonthlyLineChart({ title, subtitle, points, monthLength, stroke = "#5eb8e8" }) {
  const [tip, setTip] = useState(null);
  const padL = 48;
  const padR = 10;
  const padT = 20;
  const padB = 40;
  const cap = Math.max(Number(monthLength) || 31, 1);
  const W = chartWidthForMonthDays(cap, padL, padR);
  const H = 228;
  const iw = W - padL - padR;
  const ih = H - padT - padB;
  const xFontSize = cap > 20 ? 8 : 10;

  const ys = points.map((p) => p.y).filter((n) => Number.isFinite(n));
  if (!points.length || !ys.length) {
    return (
      <div className="mu-chart-wrap">
        <div className="mu-chart-head">{title}</div>
        {subtitle ? <div className="mu-chart-sub">{subtitle}</div> : null}
        <div className="mu-chart-empty">本月暂无有效数值点</div>
      </div>
    );
  }

  let yMin = Math.min(...ys);
  let yMax = Math.max(...ys);
  if (yMin === yMax) {
    yMin -= 1;
    yMax += 1;
  }
  const spanY = yMax - yMin || 1;
  const padY = spanY * 0.06;
  yMin -= padY;
  yMax += padY;
  const xAt = (day) => padL + ((day - 1) / (cap > 1 ? cap - 1 : 1)) * iw;
  const yAt = (v) => padT + ih - ((v - yMin) / (yMax - yMin || 1)) * ih;

  const sortedPts = [...points].sort((a, b) => a.day - b.day);
  const pathD = sortedPts
    .map((p, i) => `${i === 0 ? "M" : "L"}${xAt(p.day).toFixed(1)},${yAt(p.y).toFixed(1)}`)
    .join("");

  const fmt = (v) => {
    if (!Number.isFinite(v)) return "";
    const a = Math.abs(v);
    if (a >= 100) return v.toFixed(0);
    if (a >= 10) return v.toFixed(1);
    return v.toFixed(2);
  };
  const yTicks = [yMax, (yMax + yMin) / 2, yMin];
  const xShow = monthDayAxisTicks(cap);

  return (
    <div className="mu-chart-wrap mu-chart-wrap--scroll">
      <div className="mu-chart-head">{title}</div>
      {subtitle ? <div className="mu-chart-sub">{subtitle}</div> : null}
      <div className="mu-chart-svg-box" onMouseLeave={() => setTip(null)}>
        <svg className="mu-chart-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMinYMid meet">
          <rect x={0} y={0} width={W} height={H} fill="#fafafa" />
          {yTicks.map((tv, i) => {
            const yy = yAt(tv);
            return (
              <g key={`gy-${i}`}>
                <line x1={padL} x2={W - padR} y1={yy} y2={yy} stroke="#e5e7eb" strokeWidth={1} />
                <text x={4} y={yy + 4} fontSize={11} fill="#6b7280">
                  {fmt(tv)}
                </text>
              </g>
            );
          })}
          {xShow.map((xd) => (
            <text
              key={`gx-${xd}`}
              x={xAt(xd)}
              y={H - 6}
              fontSize={xFontSize}
              fill="#6b7280"
              textAnchor="middle"
            >
              {xd}
            </text>
          ))}
          <path
            d={pathD}
            fill="none"
            stroke={stroke}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {sortedPts.map((p) => {
            const cx = xAt(p.day);
            const cy = yAt(p.y);
            const active = tip && tip.day === p.day;
            return (
              <g
                key={`c-${p.day}`}
                className="mu-chart-pt"
                onMouseEnter={() =>
                  setTip({
                    day: p.day,
                    y: p.y,
                    leftPct: (cx / W) * 100,
                    topPct: (cy / H) * 100,
                  })
                }
              >
                <circle cx={cx} cy={cy} r={12} fill="transparent" />
                <circle
                  cx={cx}
                  cy={cy}
                  r={active ? 5 : 3.5}
                  fill={stroke}
                  stroke="#fff"
                  strokeWidth={1}
                  style={{ pointerEvents: "none" }}
                />
              </g>
            );
          })}
        </svg>
        {tip ? (
          <div
            className="mu-chart-tip"
            style={{ left: `${tip.leftPct}%`, top: `${tip.topPct}%` }}
            role="tooltip"
          >
            {tip.day}日：{fmt(tip.y)}
          </div>
        ) : null}
      </div>
    </div>
  );
}

const CHART_DAY_SUBTITLE = "按公历日；同一日取当日最后一条已通过提交";

/** 小程序随单图相对路径 → 可访问 URL（本地走 :3001） */
function entryImageSrc(path) {
  const p = String(path || "").trim();
  if (!p) return "";
  if (/^https?:\/\//i.test(p)) return p;
  const origin = String(API_BASE_URL || "").replace(/\/api\/?$/, "");
  return `${origin}${p.startsWith("/") ? p : `/${p}`}`;
}

function collectDayEntryImages(data, ymd) {
  if (!data || !ymd) return [];
  const sources = [
    { module: "在线监测", list: data.water_online_history },
    { module: "处理日报", list: data.water_lab_history },
    { module: "药剂盘点", list: data.drug_inventory },
    { module: "设备维修", list: data.repair_order?.history || [] },
  ];
  const out = [];
  const seen = new Set();
  for (const { module, list } of sources) {
    for (const sub of approvedSubmissionsOnly(list)) {
      const fillYmd = submissionFillYmdForDrug(sub.fillTime, sub.createdAt);
      if (fillYmd !== ymd) continue;
      for (const url of sub.attachmentUrls || []) {
        if (!url || seen.has(url)) continue;
        seen.add(url);
        out.push({
          url,
          src: entryImageSrc(url),
          module,
          userName: sub.userName || "—",
          fillYmd,
        });
      }
    }
  }
  return out;
}

function DrugMonthChartBlock({ catalog, submissions, kind, drugChartDrugKey, setDrugChartDrugKey, viewYm }) {
  const ym = viewYm || currentYmLocal();
  const row = pickCatalogRowByKey(catalog, drugChartDrugKey);
  if (!row) return null;
  const pts = buildDrugMonthPointsForCatalogRow(submissions, row, kind, ym);
  const flowLabel = kind === "in" ? "入库量" : "出库量";
  const stroke = kind === "in" ? "#16a34a" : "#dc2626";
  const keyVal = row.drugKey || row.slug || "";
  const { daysInMonth, monthTitle } = parseViewYm(ym);
  return (
    <div className="mu-chart-block">
      <div className="mu-chart-toolbar mb-2">
        <label className="mu-chart-select-label" htmlFor="drug-chart-select">
          折线图药剂
        </label>
        <select
          id="drug-chart-select"
          className="mu-chart-select"
          value={keyVal}
          onChange={(e) => setDrugChartDrugKey(e.target.value)}
        >
          {(catalog || []).map((c) => (
            <option key={c.drugKey || c.slug} value={c.drugKey || c.slug}>
              {c.drugName}（{c.unit}）
            </option>
          ))}
        </select>
      </div>
      <MonthlyLineChart
        title={`${monthTitle} · ${flowLabel} · ${row.drugName || ""}`}
        subtitle={CHART_DAY_SUBTITLE}
        points={pts}
        monthLength={daysInMonth}
        stroke={stroke}
      />
    </div>
  );
}

const TabSelector = ({ activeTab, onSelectTab }) => {
  const tabs = [
    { id: 'online', label: '在线监测数据', icon: <WaterQualityIcon className="w-8 h-8 mb-2" /> },
    { id: 'lab', label: '处理日报', icon: <LabIcon className="w-8 h-8 mb-2" /> },
    { id: 'inventory', label: '药剂盘点', icon: <InventoryIcon className="w-8 h-8 mb-2" /> },
    { id: 'repair', label: '设备运行与维修', icon: <RepairIcon className="w-8 h-8 mb-2" /> },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onSelectTab(tab.id)}
          className={`mu-tab ${activeTab === tab.id ? 'mu-tab--active' : ''}`}
          aria-current={activeTab === tab.id}
        >
          {tab.icon}
          <span className="font-semibold text-sm">{tab.label}</span>
        </button>
      ))}
    </div>
  );
};

const ManualUpload = () => {
  const { city } = useParams();
  const cityInfo = CITY_MAP[city];
  const cityName = cityInfo ? cityInfo.displayName : city;

  const [activeTab, setActiveTab] = useState('online');
  const [drugInventoryKind, setDrugInventoryKind] = useState('out');
  const [labChartEntryName, setLabChartEntryName] = useState(
    () => LAB_ENTRY_OPTIONS[0]?.itemName || "raw_NH3N"
  );
  const [viewYmd, setViewYmd] = useState(() => todayYmdForDrug());
  const viewYm = String(viewYmd || todayYmdForDrug()).slice(0, 7);
  const viewYmMeta = parseViewYm(viewYm);
  const [drugChartDrugKey, setDrugChartDrugKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [dayImagesOpen, setDayImagesOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const dayImages = useMemo(() => collectDayEntryImages(data, viewYmd), [data, viewYmd]);

  const apiPath = useMemo(() => {
    if (!cityInfo) return null;
    return `/manual-upload/${cityInfo.siteKey}`;
  }, [cityInfo]);

  useEffect(() => {
    let isMounted = true;
    if (!apiPath) {
      setLoading(false);
      setError('暂不支持该城市的手工上传数据。');
      return () => {};
    }

    setLoading(true);
    setError('');
    apiFetch(apiPath)
      .then((payload) => {
        if (!isMounted) return;
        setData(payload.data || {});
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err.message || '加载失败');
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [apiPath]);
  
  const renderEmpty = useCallback((text = '暂无数据') => (
    <div className="text-center py-10 px-4 text-gray-500 bg-gray-50 rounded-lg">
      <p>{text}</p>
    </div>
  ), []);

  const renderWaterOnline = useCallback(() => {
    const history = approvedSubmissionsOnly(data?.water_online_history);
    const latest = history[0];
    const raw = data?.water_online;
    let items = [];
    if (latest && Array.isArray(latest.variables)) {
      items = latest.variables;
    } else if (raw && Array.isArray(raw.variables)) {
      items = raw.variables;
    } else if (Array.isArray(raw)) {
      items = raw;
    }
    const varMap = waterOnlineVariableMap(items);
    if (!history.length && !items.length) return renderEmpty();

    const sampleStr = displayFillYmd(latest?.fillTime, latest?.createdAt);

    const ym = viewYm;
    const { daysInMonth: monthLen, monthTitle: monthLabel } = parseViewYm(viewYm);

    return (
      <div className="space-y-4">
        <div className="mu-chart-section">
          <div className="text-sm font-medium text-slate-700 mb-2">{monthLabel} · 在线监测趋势（截至今日有数据的日期）</div>
          <div className="grid md:grid-cols-3 gap-4">
            {WATER_ONLINE_DISPLAY_SCHEMA.map((row) => {
              const pts = buildWaterOnlineMonthPointsForVar(history, ym, row.name.toLowerCase());
              const colors = { PH: '#2a7ab8', NH3N: '#5eb8e8', CODCr: '#7ec8f0' };
              const stroke = colors[row.name] || '#5eb8e8';
              return (
                <MonthlyLineChart
                  key={row.name}
                  title={`${row.label}`}
                  subtitle={CHART_DAY_SUBTITLE}
                  points={pts}
                  monthLength={monthLen}
                  stroke={stroke}
                />
              );
            })}
          </div>
        </div>
        {latest ? (
          <div className="bg-gray-50 p-3 rounded-lg border flex justify-between text-sm text-gray-600">
            <span>填报人：{latest.userName || '—'}</span>
            <span>填报时间：{displayFillYmd(latest.fillTime, latest.createdAt)}</span>
          </div>
        ) : null}
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white text-sm divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">监测指标</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">数值</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">单位</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">采样时间</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {WATER_ONLINE_DISPLAY_SCHEMA.map((row) => {
                const v = varMap.get(row.name.toLowerCase());
                const unitCell = v && String(v.unit || '').trim() !== '' ? v.unit : row.unit || '';
                const valueCell = v && v.value != null && String(v.value).trim() !== '' ? String(v.value) : '—';
                return (
                  <tr key={row.name} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{row.label}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">{valueCell}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">{unitCell}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">{sampleStr}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }, [data, renderEmpty, viewYm]);

  const renderWaterLab = useCallback(() => {
    const history = approvedSubmissionsOnly(data?.water_lab_history);

    const ym = viewYm;
    const { yyStr, mmStr, daysInMonth, monthTitle } = parseViewYm(viewYm);

    const inMonth = history.filter((row) => {
      const ymd = submissionFillYmdForDrug(row.fillTime, row.createdAt);
      return ymd && ymd.slice(0, 7) === ym;
    });

    const byDay = new Map();
    for (const row of inMonth) {
      const ymdKey = submissionFillYmdForDrug(row.fillTime, row.createdAt);
      if (!ymdKey) continue;
      if (!byDay.has(ymdKey)) byDay.set(ymdKey, []);
      byDay.get(ymdKey).push(row);
    }
    for (const arr of byDay.values()) {
      arr.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
    }

    const todayYmd = todayYmdForDrug();

    const labEntry =
      LAB_ENTRY_OPTIONS.find((o) => o.itemName === labChartEntryName) || LAB_ENTRY_OPTIONS[0];
    const labPoints = buildWaterLabMonthPointsForItem(history, ym, labEntry?.itemName);

    return (
      <div className="jh-daily-wrap overflow-x-auto rounded-lg border border-gray-200">
        <div className="mu-chart-section px-3 pt-3 pb-2 border-b border-gray-200 bg-white">
          <div className="text-sm font-medium text-slate-700 mb-2">月折线图</div>
          <div className="mu-chart-toolbar">
            <label className="mu-chart-select-label" htmlFor="lab-chart-entry-select">
              录入种类
            </label>
            <select
              id="lab-chart-entry-select"
              className="mu-chart-select"
              value={labEntry?.itemName || ""}
              onChange={(e) => setLabChartEntryName(e.target.value)}
            >
              {LAB_ENTRY_OPTIONS.map((o) => (
                <option key={o.itemName} value={o.itemName}>
                  {o.optionLabel}
                </option>
              ))}
            </select>
          </div>
          <MonthlyLineChart
            title={`${monthTitle} · ${labEntry?.optionLabel || ""}`}
            subtitle={CHART_DAY_SUBTITLE}
            points={labPoints}
            monthLength={daysInMonth}
            stroke="#5eb8e8"
          />
        </div>
        <div className="text-sm font-medium text-slate-700 px-3 py-2 bg-slate-100 border-b border-gray-200">
          {monthTitle} · 工序检测（共 {daysInMonth} 天，按公历法定日序{viewYm === currentYmLocal() ? "；数据截至今日" : ""}）
        </div>
        <table className="jh-daily-table min-w-full text-xs text-gray-800 bg-white">
          <thead>
            <tr className="bg-slate-100">
              <th rowSpan={2} className="jh-sticky-date px-2 py-2 border border-gray-200 font-semibold whitespace-nowrap">
                日期
              </th>
              <th rowSpan={2} className="px-2 py-2 border border-gray-200 font-semibold whitespace-nowrap bg-slate-50">
                填报人
              </th>
              {PROCESS_DAILY_GROUPS.map((g) => (
                <th
                  key={g.title}
                  colSpan={g.cols.length}
                  className="px-2 py-2 border border-gray-200 font-semibold text-center whitespace-nowrap"
                >
                  {g.title}
                </th>
              ))}
            </tr>
            <tr className="bg-gray-50">
              {PROCESS_DAILY_GROUPS.flatMap((g) =>
                g.cols.map((c) => (
                  <th key={c.name} className="px-2 py-2 border border-gray-200 font-medium whitespace-nowrap">
                    {c.label}
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).flatMap((day) => {
              const ymdKey = `${yyStr}-${mmStr}-${pad2Drug(day)}`;
              const dateLabel = formatNationalDayLabel(ymdKey);
              const isAfterToday = isFutureDayRow(ymdKey, viewYm, todayYmd);

              if (isAfterToday) {
                return [
                  <tr key={`${ymdKey}-future`} className="bg-gray-50/30">
                    <td className="jh-sticky-date px-2 py-1.5 border border-gray-200 whitespace-nowrap bg-white text-gray-800">
                      {dateLabel}
                    </td>
                    <td className="px-2 py-1.5 border border-gray-200 whitespace-nowrap" />
                    {PROCESS_DAILY_GROUPS.flatMap((g) =>
                      g.cols.map((c) => {
                        const wide = c.name === "remarks";
                        return (
                          <td
                            key={`${ymdKey}-f-${c.name}`}
                            className={`px-2 py-1.5 border border-gray-200 text-center ${wide ? "min-w-[140px] max-w-[220px] text-left whitespace-normal break-words" : "whitespace-nowrap"}`}
                          />
                        );
                      })
                    )}
                  </tr>,
                ];
              }

              const subs = byDay.get(ymdKey) || [];

              if (!subs.length) {
                return [
                  <tr key={`${ymdKey}-empty`} className="bg-slate-50/50">
                    <td className="jh-sticky-date px-2 py-1.5 border border-gray-200 whitespace-nowrap bg-white text-gray-800">
                      {dateLabel}
                    </td>
                    <td className="px-2 py-1.5 border border-gray-200 whitespace-nowrap text-gray-500">未填写</td>
                    {PROCESS_DAILY_GROUPS.flatMap((g) =>
                      g.cols.map((c) => {
                        const wide = c.name === "remarks";
                        return (
                          <td
                            key={`${ymdKey}-e-${c.name}`}
                            className={`px-2 py-1.5 border border-gray-200 text-center text-gray-500 ${wide ? "min-w-[140px] max-w-[220px] text-left whitespace-normal break-words" : "whitespace-nowrap"}`}
                          >
                            "未填写"                          </td>
                        );
                      })
                    )}
                  </tr>,
                ];
              }

              return subs.map((row) => (
                <tr key={row.submissionId || `${ymdKey}-${row.createdAt}`} className="mu-row-hover">
                  <td className="jh-sticky-date px-2 py-1.5 border border-gray-200 whitespace-nowrap bg-white">
                    {dateLabel}
                  </td>
                  <td className="px-2 py-1.5 border border-gray-200 whitespace-nowrap max-w-[120px] truncate" title={row.userName || ""}>
                    {row.userName && String(row.userName).trim() ? row.userName : "未填写"}
                  </td>
                  {PROCESS_DAILY_GROUPS.flatMap((g) =>
                    g.cols.map((c) => {
                      const cell = pickProcessDailyCell(row, c.name);
                      const wide = c.name === "remarks";
                      return (
                        <td
                          key={`${row.submissionId}-${c.name}`}
                          className={`px-2 py-1.5 border border-gray-200 text-center ${wide ? "min-w-[140px] max-w-[220px] text-left whitespace-normal break-words" : "whitespace-nowrap"}`}
                        >
                          {cell ? cell : "未填写"}
                        </td>
                      );
                    })
                  )}
                </tr>
              ));
            })}
          </tbody>
        </table>
      </div>
    );
  }, [data, labChartEntryName, viewYm]);

  const renderDrugInventory = useCallback(() => {
    const catalog = data?.drug_catalog;
    const submissions = approvedSubmissionsOnly(data?.drug_inventory);
    const noApprovedMsg = data?.drug_inventory?.length
      ? "暂无已通过审核的药剂提交记录；待审核数据不会在网页展示。"
      : undefined;

    if (!Array.isArray(catalog) || !catalog.length) {
      if (!submissions.length) return renderEmpty(noApprovedMsg);
      const submission = pickApprovedDrugSubmission(submissions, { ymd: viewYmd, mode: "asOf" });
      if (!submission) {
        return renderEmpty("暂无已通过审核、且填报日期不晚于所选日期的药剂提交记录。");
      }
      return (
        <div>
          <div className="bg-gray-50 p-3 rounded-t-lg border-b flex justify-between text-sm text-gray-600">
            <span>填报人：{submission.userName || "—"}</span>
            <span>填报时间：{displayFillYmd(submission.fillTime, submission.createdAt)}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white text-sm divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">药剂名称</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">入库量</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">出库量</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">系统累计库存</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">单位</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(submission.records || []).map((record) => (
                  <tr key={record.recordId || record.drugName} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap font-medium text-gray-900">{record.drugName || "—"}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-green-600">{record.inQuantity ?? "—"}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-red-600">{record.outQuantity ?? "—"}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-gray-700 font-semibold">
                      {formatDrugStockCell(record.stockQuantity)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-gray-700">{record.unit || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (!submissions.length) return renderEmpty(noApprovedMsg || "暂无已通过审核的药剂提交记录；待审核数据不会在网页展示。");

    const kind = drugInventoryKind === "in" ? "in" : "out";
    const submission = pickApprovedDrugSubmission(submissions, { ymd: viewYmd, kind, mode: "onDay" });
    const rows = buildDrugInventoryTabRows(catalog, submission, kind, submissions, viewYm, viewYmd);
    const flowLabel = kind === "in" ? "入库量" : "出库量";
    const monthCumLabel = kind === "in" ? "本月累计入库" : "本月累计出库";

    return (
      <div>
        <div className="mu-inventory-kind-toggle" role="tablist" aria-label="药剂盘点类型">
          <button
            type="button"
            role="tab"
            aria-selected={drugInventoryKind === "out"}
            onClick={() => setDrugInventoryKind("out")}
            className={`mu-inventory-kind-btn${drugInventoryKind === "out" ? " is-active" : ""}`}
          >
            出库盘点
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={drugInventoryKind === "in"}
            onClick={() => setDrugInventoryKind("in")}
            className={`mu-inventory-kind-btn${drugInventoryKind === "in" ? " is-active" : ""}`}
          >
            入库盘点
          </button>
        </div>
        <DrugMonthChartBlock
          catalog={catalog}
          submissions={submissions}
          kind={kind}
          drugChartDrugKey={drugChartDrugKey}
          setDrugChartDrugKey={setDrugChartDrugKey}
          viewYm={viewYm}
        />
        <div className="bg-gray-50 p-3 rounded-t-lg border-b flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
          <span>填报人：{submission?.userName || "—"}</span>
          <label className="inline-flex flex-wrap items-center gap-2">
            <span>查看日期</span>
            <input
              type="date"
              className="mu-month-input"
              value={viewYmd}
              max={todayYmdForDrug()}
              onChange={(e) => {
                const v = e.target.value;
                if (v) setViewYmd(v);
              }}
            />
            {!submission ? (
              <span className="text-amber-700">该日暂无已通过的{kind === "in" ? "入库" : "出库"}填报</span>
            ) : null}
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white text-sm divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">药剂名称</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{flowLabel}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{monthCumLabel}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">系统累计库存</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">单位</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((record, idx) => (
                <tr key={`${record.drugName}-${record.unit}-${idx}`} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap font-medium text-gray-900">{record.drugName || "—"}</td>
                  <td className={`px-4 py-4 whitespace-nowrap ${kind === "in" ? "text-green-600" : "text-red-600"}`}>
                    {record.flowQty != null && String(record.flowQty).trim() !== "" ? record.flowQty : "—"}
                  </td>
                  <td className={`px-4 py-4 whitespace-nowrap font-semibold ${kind === "in" ? "text-green-700" : "text-red-700"}`}>
                    {formatDrugStockCell(record.monthCumQty)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-gray-700 font-semibold">
                    {formatDrugStockCell(record.stockQuantity)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-gray-700">{record.unit || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-gray-500 px-3 py-2 bg-gray-50 border-t border-gray-200">
            按所选日展示已通过的{flowLabel}；「{monthCumLabel}」为当月截至该日合计；系统累计库存＝已通过入库合计减出库合计。
          </p>
        </div>
      </div>
    );
  }, [data, renderEmpty, drugInventoryKind, drugChartDrugKey, viewYm, viewYmd]);

  const renderRepairOrder = useCallback(() => {
    const repairOrder = data?.repair_order;
    const latest = repairOrder && repairOrder.latest;

    if (cityInfo?.siteKey === 'jinghong') {
      const rowRepairMap = extractJinghongPerDeviceRepairsFromLatest(latest);
      const fmtNum = (v) => {
        if (v == null || Number.isNaN(v)) return '—';
        if (typeof v === 'number' && !Number.isInteger(v)) return String(v);
        return String(v);
      };
      return (
        <div className="space-y-4">
          {latest && (latest.userName || latest.fillTime || latest.createdAt) ? (
            <div className="bg-gray-50 p-3 rounded-lg border flex justify-between text-sm text-gray-600">
              <span>填报人：{latest.userName || '—'}</span>
              <span>填报时间：{displayFillYmd(latest.fillTime, latest.createdAt)}</span>
            </div>
          ) : null}
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full bg-white text-xs text-gray-800">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-2 py-2 border border-gray-200 text-left font-semibold whitespace-nowrap">设备名称</th>
                  <th className="px-2 py-2 border border-gray-200 text-center font-semibold whitespace-nowrap">故障情况</th>
                  <th className="px-2 py-2 border border-gray-200 text-center font-semibold whitespace-nowrap">是否维修</th>
                  <th className="px-2 py-2 border border-gray-200 text-left font-semibold">用途</th>
                  <th className="px-2 py-2 border border-gray-200 text-right font-semibold whitespace-nowrap">数量（台）</th>
                  <th className="px-2 py-2 border border-gray-200 text-right font-semibold whitespace-nowrap">功率（kw）</th>
                  <th className="px-2 py-2 border border-gray-200 text-right font-semibold whitespace-nowrap">使用时长（h/天）</th>
                  <th className="px-2 py-2 border border-gray-200 text-right font-semibold whitespace-nowrap">耗电量（kw/h·月）</th>
                </tr>
              </thead>
              <tbody>
                {JINGHONG_EQUIPMENT_CATALOG.map((row, idx) => {
                  const label = jinghongSubmitDeviceLabel(row);
                  const fr = rowRepairMap.get(label) || JINGHONG_MINI_EMPTY;
                  return (
                    <tr key={`${row.name}-${row.purpose}-${idx}`} className="hover:bg-slate-50/60">
                      <td className="px-2 py-1.5 border border-gray-200 font-medium whitespace-nowrap">{row.name}</td>
                      <td className="px-2 py-1.5 border border-gray-200 text-center whitespace-nowrap">{fr.fault}</td>
                      <td className="px-2 py-1.5 border border-gray-200 text-center whitespace-nowrap">{fr.repair}</td>
                      <td className="px-2 py-1.5 border border-gray-200 text-left">{row.purpose}</td>
                      <td className="px-2 py-1.5 border border-gray-200 text-right whitespace-nowrap">{fmtNum(row.qty)}</td>
                      <td className="px-2 py-1.5 border border-gray-200 text-right whitespace-nowrap">{fmtNum(row.powerKw)}</td>
                      <td className="px-2 py-1.5 border border-gray-200 text-right whitespace-nowrap">{fmtNum(row.hoursPerDay)}</td>
                      <td className="px-2 py-1.5 border border-gray-200 text-right whitespace-nowrap">{fmtNum(row.kwhMonth)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (!latest || !(latest.sections || []).length) return renderEmpty();

    return (
      <div className="space-y-6">
        <div className="bg-gray-50 p-3 rounded-lg border flex justify-between text-sm text-gray-600">
          <span>填报人：{latest.userName || '—'}</span>
          <span>填报时间：{displayFillYmd(latest.fillTime, latest.createdAt)}</span>
        </div>
        {latest.sections.map((section) => {
          const devices = sortRepairDevices(section.devices);
          const flatRows = devices.flatMap((device) =>
            (device.items || []).map((item) => ({ device, item }))
          );
          return (
            <div key={section.title || 'sec'}>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">{section.title || '设备巡检'}</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white text-sm divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">设备名称</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">序号</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">故障情况</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">是否维修</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {!flatRows.length ? (
                      <tr>
                        <td colSpan={4} className="text-center py-4 text-gray-500">无设备数据</td>
                      </tr>
                    ) : (
                      flatRows.map(({ device, item }) => (
                        <tr
                          key={`${section.title}-${device.name}-${item.idx}`}
                          className="hover:bg-gray-50"
                        >
                          <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{device.name || '—'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-700">{item.idx ?? '—'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-700">{item.fault || '正常'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-700">{item.repair || '—'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [data, renderEmpty, cityInfo]);

  const renderContent = () => {
    switch (activeTab) {
      case 'online': return renderWaterOnline();
      case 'lab': return renderWaterLab();
      case 'inventory': return renderDrugInventory();
      case 'repair': return renderRepairOrder();
      default: return renderEmpty('请选择一个数据类别');
    }
  };

  const handleExportData = useCallback(() => {
    if (!data || !cityInfo) return;
    downloadManualUploadExcel({
      data,
      cityName,
      siteKey: cityInfo.siteKey,
      viewYm,
    });
  }, [data, cityInfo, cityName, viewYm]);

  return (
    <div className="min-h-screen bg-gray-100 font-sans flex flex-col">
      <Navbar />
      <main className="flex-1">
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
            <div className="px-4 py-6 sm:px-0">
                <h1 className="text-2xl font-bold text-gray-900 leading-tight mb-6">
                    {cityName} · 手工上传数据
                </h1>
                {!cityInfo ? (
                    <div className="bg-white shadow rounded-lg p-6 text-center text-red-600">
                        {renderEmpty('暂不支持该城市的数据展示。')}
                    </div>
                ) : (
                    <>
                        <TabSelector activeTab={activeTab} onSelectTab={setActiveTab} />
                        <div className="flex flex-wrap mu-toolbar">
                          <span className="mu-toolbar-label">查看日期</span>
                          <input
                            type="date"
                            className="mu-month-input"
                            value={viewYmd}
                            max={todayYmdForDrug()}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v) setViewYmd(v);
                            }}
                          />
                          <span className="mu-toolbar-hint">
                            药剂表按所选日；日序/折线按 {viewYmMeta.monthTitle}（{viewYmMeta.daysInMonth} 天）
                            {viewYm !== currentYmLocal() ? "；历史月整月" : "；当前月今日之后留空"}
                          </span>
                          <button
                            type="button"
                            className="mu-images-btn"
                            disabled={loading || !!error || !data}
                            onClick={() => {
                              setPreviewImage(null);
                              setDayImagesOpen(true);
                            }}
                            title="查看所选日期的随单上传图片"
                          >
                            查看当日图片{dayImages.length ? `（${dayImages.length}）` : ""}
                          </button>
                          <button
                            type="button"
                            className="mu-export-btn"
                            disabled={loading || !!error || !data}
                            onClick={handleExportData}
                            title="按正式表版式导出（药剂出库/入库、实验数据、水量日统计、水量结算）"
                          >
                            按正式表版式导出
                          </button>
                        </div>
                        <div className="bg-white shadow-lg rounded-lg p-6">
                        {loading && <div className="text-center py-10 text-gray-600">数据加载中…</div>}
                        {!loading && error && <div className="text-center py-10 text-red-600 bg-red-50 p-4 rounded-md">{error}</div>}
                        {!loading && !error && renderContent()}
                        </div>
                    </>
                )}
            </div>
        </div>
      </main>
      <Footer />
      {dayImagesOpen ? (
        <div
          className="mu-img-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={`${viewYmd} 随单图片`}
          onClick={() => {
            setDayImagesOpen(false);
            setPreviewImage(null);
          }}
        >
          <div className="mu-img-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mu-img-modal-head">
              <div>
                <h2 className="mu-img-modal-title">{viewYmd} · 随单图片</h2>
                <p className="mu-img-modal-sub">仅展示该日已通过审核提交中的图片</p>
              </div>
              <button
                type="button"
                className="mu-img-modal-close"
                aria-label="关闭"
                onClick={() => {
                  setDayImagesOpen(false);
                  setPreviewImage(null);
                }}
              >
                ×
              </button>
            </div>
            {dayImages.length === 0 ? (
              <div className="mu-img-modal-empty">该日暂无随单图片</div>
            ) : (
              <div className="mu-img-grid">
                {dayImages.map((img) => (
                  <button
                    key={img.url}
                    type="button"
                    className="mu-img-card"
                    onClick={() => setPreviewImage(img)}
                    title={`${img.module} · ${img.userName}`}
                  >
                    <img
                      src={img.src}
                      alt={`${img.module} ${img.fillYmd}`}
                      loading="lazy"
                      onError={(e) => {
                        const el = e.currentTarget;
                        el.style.display = "none";
                        const sib = el.nextElementSibling;
                        if (sib && sib.classList.contains("mu-img-missing")) {
                          sib.hidden = false;
                        }
                      }}
                    />
                    <span className="mu-img-missing" hidden>
                      文件缺失
                    </span>
                    <span className="mu-img-card-meta">
                      {img.module} · {img.userName}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
      {previewImage ? (
        <div
          className="mu-img-preview-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={() => setPreviewImage(null)}
        >
          <img
            className="mu-img-preview"
            src={previewImage.src}
            alt={previewImage.module}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ) : null}
    </div>
  );
};

export default ManualUpload;